"use client"

import { useId, useMemo, useRef, useEffect, useState } from "react"

interface SparklineProps {
  data: number[]
  height?: number
  width?: number | "100%"
  color?: string
  showFill?: boolean
  strokeWidth?: number
  animateDraw?: boolean
}

export default function Sparkline({
  data,
  height = 36,
  width = 132,
  color = "rgb(16,185,129)",
  showFill = true,
  strokeWidth = 1.5,
  animateDraw = false,
}: SparklineProps) {
  const gradientId = useId()
  const pathRef = useRef<SVGPathElement | null>(null)
  const [dashLength, setDashLength] = useState<number | null>(null)
  const isFluid = width === "100%"
  const viewBoxW = isFluid ? 100 : (typeof width === "number" ? width : 100)

  const path = useMemo(() => {
    if (data.length < 2) return { line: "", area: "" }
    const max = Math.max(...data, 1)
    const stepX = viewBoxW / (data.length - 1)
    const pts = data.map((v, i) => {
      const x = i * stepX
      const y = height - (v / max) * (height - 4) - 2
      return [x, y] as const
    })
    const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ")
    const area = `${line} L${viewBoxW},${height} L0,${height} Z`
    return { line, area }
  }, [data, viewBoxW, height])

  useEffect(() => {
    if (!animateDraw || !pathRef.current) return
    try {
      const len = pathRef.current.getTotalLength()
      setDashLength(len)
    } catch {
      setDashLength(viewBoxW * 1.5)
    }
  }, [animateDraw, path.line, viewBoxW])

  if (!path.line) {
    return <div className="rounded bg-muted/30" style={{ height, width: isFluid ? "100%" : width }} />
  }

  return (
    <svg
      width={isFluid ? "100%" : viewBoxW}
      height={height}
      viewBox={`0 0 ${viewBoxW} ${height}`}
      preserveAspectRatio={isFluid ? "none" : "xMidYMid meet"}
      className="overflow-visible"
    >
      {showFill && (
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.18" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
      )}
      {showFill && (
        <path
          d={path.area}
          fill={`url(#${gradientId})`}
          className={animateDraw ? "animate-fade-in" : ""}
          style={animateDraw ? { animationDelay: "350ms" } : undefined}
        />
      )}
      <path
        ref={pathRef}
        d={path.line}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
        className={animateDraw && dashLength !== null ? "animate-draw" : ""}
        style={
          animateDraw && dashLength !== null
            ? ({
                strokeDasharray: dashLength,
                ["--dash-length" as string]: dashLength,
              } as React.CSSProperties)
            : undefined
        }
      />
    </svg>
  )
}
